import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

import { trackPageview, trackUserEventPageView } from 'dibs-tracking';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2.ts';
import {
    pageTypeConstants as PAGE_TYPES,
    pageTypeConstants,
} from '../constants/pageTypeConstants.ts';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData.ts';
import { ServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext.tsx';
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader.tsx';
import { handlePageRefetchVariableOnUserChange } from '../shared/handlePageRefetchVariableOnUserChange.ts';

import { updateRelayVariables } from '../actions/sharedRelayVariableActions.js';
import { toggleRequestInFlight, updateUriRef } from '../actions/filterActions.ts';

import { getNewPaginationUriRef, addCdnQueryParams } from '../utils/uriUtils.ts';

import { FindingRouter } from '../router/FindingRouter.js';
import {
    personalizedRerankTracking,
    trackUrgencySignals,
    trackShippingCostSb,
    trackContentVisibilitySb,
    trackShipsFromSBTile,
} from '../finding/SbSharedTrackingFunctions/SbSharedTrackingFunctions.ts';
import { trackEligibleForRerankEvent } from '../utils/tracking/searchBrowse/addPersonalizationTrackingData.ts';

import { handleSbUserCountryCode } from '../finding/helpers/handleSbUserCountryCode.ts';
import { trackSeoTests } from '../pdp/helpers/trackSeoTests.ts';

class BuySharedRefetchContainerComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleBuyPageRefetch = this.handleBuyPageRefetch.bind(this);
        this.refetchCallback = this.refetchCallback.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);

        this.hasFiredEligibleForRerankEvent = createRef();
    }

    componentDidMount() {
        const { buyPage, relay } = this.props;
        const {
            pageType,
            totalResults,
            appliedFilters,
            displayUriRef,
            appliedPersonalizationList,
        } = buyPage;
        this.buyPageName = appliedFilters.find(
            f => f.name === PAGE_TYPES.BUY
        )?.values?.[0]?.urlLabel;
        this.router = new FindingRouter({ routes: [{ path: '/buy/:pageName?' }] });
        this.props.updateUriRef({ uriRef: displayUriRef });
        trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
                personalizationType: appliedPersonalizationList?.length
                    ? appliedPersonalizationList.join(',')
                    : null,
            },
        });

        trackUserEventPageView(relay.environment);

        personalizedRerankTracking({
            pageType: pageTypeConstants.BUY,
            sortValue: buyPage?.sort?.currentOption?.urlLabel,
        });
        trackUrgencySignals();
        trackShippingCostSb();
        trackContentVisibilitySb();
        trackShipsFromSBTile();

        buyPage.trackedAbTests?.forEach(abTest => {
            trackAbTestV2Variant(abTest);
        });
    }

    componentDidUpdate(prevProps) {
        const { generatedUriRef, page: newPage, viewer, buyPage, relayVariables } = this.props;
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged = oldUriRef !== '' && newUriRef !== oldUriRef;
        const pageChanged = newPage !== prevProps.page;
        let uriRef = generatedUriRef;

        if (pageChanged) {
            uriRef = getNewPaginationUriRef(generatedUriRef, newPage);
            this.handlePageChange(newPage);
        }

        if (uriRefChanged) {
            const variables = { uriRef, page: newPage };
            this.props.toggleRequestInFlight(true);
            this.handleBuyPageRefetch(
                { ...relayVariables, ...variables, fetchBuy: true },
                null,
                this.refetchCallback
            );
            this.props.updateRelayVariables(variables);
        }

        if (viewer?.isEligibleForRerank && !this.hasFiredEligibleForRerankEvent.current) {
            trackEligibleForRerankEvent();
            this.hasFiredEligibleForRerankEvent.current = true;
        }

        buyPage.trackedAbTests?.forEach(abTest => {
            if (abTest && !prevProps.buyPage.trackedAbTests?.includes(abTest)) {
                trackAbTestV2Variant(abTest);
            }
        });
        trackSeoTests({ seoAbTestV2List: buyPage.seoAbTestV2List });
    }

    refetchCallback() {
        this.props.toggleRequestInFlight(false);
    }

    handlePageChange(page) {
        if (this.buyPageName) {
            this.router.navigate(
                getNewPaginationUriRef(`/${PAGE_TYPES.BUY}/${this.buyPageName}/`, page),
                { replace: true }
            );
        }
    }

    handleUserRefetch(variables, renderVariables, callback) {
        const { buyPage, relayVariables } = this.props;
        const { displayUriRef } = buyPage || {};
        const { followSearchPages } = relayVariables || {};
        //Personalized-rerank needs to refetch s&b after login
        const fetchBuy =
            handlePageRefetchVariableOnUserChange(variables) || variables.fetchSbOnUserChange;
        this.handleBuyPageRefetch(
            {
                ...variables,
                fetchBuy,
                skipUser: false,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,

                userCountryCode: handleSbUserCountryCode({
                    userCountryCode: variables?.userCountryCode,
                    fetchSb: variables?.fetchSb,
                }),
            },
            renderVariables,
            callback
        );
    }

    handleBuyPageRefetch(variables, renderVariables, callback) {
        this.props.refetch(variables, renderVariables, callback);
    }

    render() {
        return <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />;
    }
}

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;

    return {
        page,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
}

const mapDispatchToProps = {
    toggleRequestInFlight,
    updateUriRef,
    updateRelayVariables,
};

BuySharedRefetchContainerComponent.propTypes = {
    relay: PropTypes.object.isRequired,
    refetch: PropTypes.func.isRequired,
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateUriRef: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    buyPage: PropTypes.object,
    viewer: PropTypes.object,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
        userCountryCode: PropTypes.string,
        priceBookName: PropTypes.string,
    }).isRequired,
};

BuySharedRefetchContainerComponent.contextType = ServerVarsContext;

export const BuySharedRefetchContainer = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(BuySharedRefetchContainerComponent),
    {
        viewer: graphql`
            fragment BuySharedRefetchContainer_viewer on Viewer {
                isEligibleForRerank(userId: $rerankUserId, guestId: $rerankGuestId)
                    @include(if: $isClient)
            }
        `,
        buyPage: graphql`
            fragment BuySharedRefetchContainer_buyPage on ItemSearchQueryConnection {
                trackedAbTests
                pageType
                totalResults
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
                sort {
                    currentOption {
                        urlLabel
                    }
                }
                displayUriRef
                appliedPersonalizationList
                seoAbTestV2List {
                    ...trackSeoTests_abTestConfigV2UserVariant
                }
            }
        `,
    }
);
